import styles from '../drawermenu.module.css';

import React, { useState, useEffect, useRef } from "react";
import { isArrayEmpty, isNullOrEmpty } from "../../../../helpers/ObjectHelpers";
import { isMobile } from '../../../../helpers/DeviceHelpers';
import { DrawerMenuItemIcon } from "./DrawerMenuItemIcon";
import Icons from "../../../icons/Icons";
import Slide from "../../../Slide";
import { IconColors } from "../../../../../services/SystemNames";
import parse from 'html-react-parser';
import { NavLink } from "react-router";

interface DrawerMenuItemProps {
    itemProps: any,
    onClick: (e: any) => void,
    isNewVersionAvailable: boolean,
    isMenuExpanded: boolean,
    mini: boolean,
    className?: string,
    paddingLeft?: number,
}
export const DrawerMenuItem = ({
    itemProps,
    onClick,
    isNewVersionAvailable,
    isMenuExpanded,
    mini,
    className = "",
    paddingLeft = 0,
}: DrawerMenuItemProps) => {

    const hasChildren = !isArrayEmpty(itemProps.children);
    const hasNavLink = !isNullOrEmpty(itemProps.to);

    return (
        hasChildren ?
            <DrawerMenuItemParentContent
                itemProps={itemProps}
                className={className}
                paddingLeft={paddingLeft}
                onClick={onClick}
                isNewVersionAvailable={isNewVersionAvailable}
                isMenuExpanded={isMenuExpanded}
                mini={mini}
            />
            :
            hasNavLink ?
                <DrawerMenuItemWithNavLinkContent
                    itemProps={itemProps}
                    className={className}
                    paddingLeft={paddingLeft} />
                    :
                <DrawerMenuItemWithOnClickContent
                    itemProps={itemProps}
                    className={className}
                    paddingLeft={paddingLeft}
                    onClick={onClick}
                    isNewVersionAvailable={isNewVersionAvailable}
                />
    );
}

interface DrawerMenuItemParentContentProps {
    itemProps: any;
    className: string;
    paddingLeft: number;
    isNewVersionAvailable: boolean;
    isMenuExpanded: boolean,
    mini: boolean,
    onClick: (e: any) => void;
    onExpand?: () => void;
}
const DrawerMenuItemParentContent = ({
    itemProps,
    className,
    paddingLeft,
    isNewVersionAvailable,
    isMenuExpanded,
    mini,
    onClick,
    onExpand
}: DrawerMenuItemParentContentProps) => {

    const [isMenuItemExpanded, setIsMenuItemExpanded] = useState<boolean>(false);
    const newPaddingLeft = paddingLeft + 20;

    const handleOnExpand = () => {
        setIsMenuItemExpanded(true);
        if(!onExpand) return;
        onExpand();
    }

    useEffect(() => {
        if(isMenuExpanded || !mini)return;
        setIsMenuItemExpanded(false);
    }, [isMenuExpanded, mini]);

    return (
        <>
            <li>
                <div className={`k-drawer-item ${(isMobile()) ? styles.drawerItemNoHover : ""}`} role="menuitem"
                    style={{ display: "flex" }}>
                <div
                    className={`${styles.drawerMenuItem} ${className}`}
                    onClick={() => setIsMenuItemExpanded(!isMenuItemExpanded)}
                    style={{ paddingLeft: paddingLeft }}>
                        <DrawerMenuItemItemPropContent
                            itemProps={itemProps}
                            isMenuItemExpanded={isMenuItemExpanded}
                        />
                    </div>
                </div>
            </li>
            {
                <Slide show={isMenuItemExpanded}>
                    <li className="k-drawer-item k-drawer-separator" role="separator"></li>
                    {
                        itemProps.children.map((child: any, index: number) => {
                            if (!isNullOrEmpty(child.children)) {
                                return (
                                    <DrawerMenuItemParentContent
                                        key={index}
                                        itemProps={child}
                                        className={className}
                                        paddingLeft={newPaddingLeft}
                                        onClick={onClick}
                                        isNewVersionAvailable={isNewVersionAvailable}
                                        onExpand={handleOnExpand}
                                    />
                                )
                            } else {
                                if (!isNullOrEmpty(child.to)) {
                                    return (
                                        <DrawerMenuItemWithNavLinkContent
                                            key={index}
                                            itemProps={child}
                                            className={styles.drawerMenuItemSub}
                                            paddingLeft={newPaddingLeft}
                                            onExpand={handleOnExpand}
                                        />
                                    );
                                } else {
                                    return (
                                        <DrawerMenuItemWithOnClickContent
                                            key={index}
                                            itemProps={child}
                                            className={styles.drawerMenuItemSub}
                                            paddingLeft={newPaddingLeft}
                                            isNewVersionAvailable={isNewVersionAvailable}
                                            onClick={onClick}
                                        />
                                    );
                                }
                            }
                        })
                    }
                    <li className="k-drawer-item k-drawer-separator" role="separator"></li>
                </Slide>
            }
        </>
    )
}


interface DrawerMenuItemWithNavLinkContentProps {
    itemProps: any;
    className: string;
    paddingLeft: number;
    onExpand?: () => void;
}
const DrawerMenuItemWithNavLinkContent = ({
    itemProps,
    className,
    paddingLeft,
    onExpand
}: DrawerMenuItemWithNavLinkContentProps) => {

    const navRef = useRef<HTMLAnchorElement>(null);

    useEffect(() => {
        if (navRef.current && navRef.current.classList.contains(styles.drawerMenuItemSelected)) {
            setTimeout(() => {
                navRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                }, 500);
            if(!onExpand) return;
            onExpand();
        }
    }, []);

    return (
        <li>
            <NavLink
                ref={navRef}
                to={itemProps.to}
                className={
                    ({ isActive }) => isActive ? styles.drawerMenuItemSelected : ``
                }>
                <div className={`k-drawer-item `} role="menuitem" style={{ display: "flex" }}>
                    <div
                        className={`${styles.drawerMenuItem} ${className}`}
                        style={{ paddingLeft: paddingLeft }}>
                        <DrawerMenuItemItemPropContent
                            itemProps={itemProps}
                        />
                    </div>
                </div>
            </NavLink>
        </li>
    )
}

interface DrawerMenuItemWithOnClickContentProps {
    itemProps: any;
    className: string;
    paddingLeft: number;
    isNewVersionAvailable: boolean;
    onClick: (e: any) => void;
}
const DrawerMenuItemWithOnClickContent = ({
    itemProps,
    className,
    paddingLeft,
    isNewVersionAvailable,
    onClick
}: DrawerMenuItemWithOnClickContentProps) => {


    const isNewVersion = itemProps.type === "NEW_VERSION" && !isNewVersionAvailable;

    return (
        <li className={`k-drawer-item ${isNewVersion ? styles.drawerItemNewVersion : ""}`} role="menuitem"
            style={{ display: isNewVersion ? "none" : "flex" }}>
            <div
                className={`${styles.drawerMenuItem} ${className}`}
                onClick={() => onClick(itemProps)}
                style={{ paddingLeft: paddingLeft }}>
                <DrawerMenuItemItemPropContent
                    itemProps={itemProps}
                />
            </div>
        </li>
    )
}

interface DrawerMenuItemItemPropContent {
    itemProps: any;
    isMenuItemExpanded?: boolean;
}
const DrawerMenuItemItemPropContent = ({
        itemProps,
        isMenuItemExpanded = false
    }: DrawerMenuItemItemPropContent) => {

    const hasChildren = !isArrayEmpty(itemProps.children);

    return (
        <>
            <div className="text-center">
                {
                    (!isNullOrEmpty(itemProps.icon)) &&
                    <div className={`${styles.drawerMenuItemIcon}`}>
                        <div className={`${styles.drawerMenuItemIconContent}`}>
                            <DrawerMenuItemIcon iconName={itemProps.icon!} />
                        </div>
                        {
                            (itemProps.beta) &&
                            <div className={`${styles.drawerMenuItemIconSub}`}>B</div>
                        }
                    </div>
                }
            </div>
            <div className={`${styles.drawerMenuItemDesc}`}>
                {parse(itemProps.title)}
                {
                    (itemProps.beta) &&
                    <>&nbsp;<sup>beta</sup></>
                }
            </div>
            <div>
                {
                    (hasChildren) &&
                        <div style={{ paddingRight: 10, display: "grid", justifyContent: "space-around" }}>
                            <Icons color={IconColors.Primary} iconName={isMenuItemExpanded ? "chevronUp" : "chevronDown"} dimensions={24} />
                        </div>
                }
            </div>
        </>
    )
}
